import { useAtomValue, useSetAtom } from 'jotai';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button, InfoTip } from '@/core/ui';
import { BasemapField } from '@/features/basemap';
import { WindHelp, WindLegend } from '@/features/forecast';
import { ClearanceField, GlideRatioField, ModeField } from '@/features/glide';
import { StationsField } from '@/features/stations';
import { TrailsField } from '@/features/trails';
import { UnitsField } from '@/features/units';
import { canClearAtom, clearAllAtom } from './actions';
import { appModeAtom } from './atoms';
import { AppModeToggle } from './AppModeToggle';
import { TrashIcon } from './icons';

/**
 * What matters while working on the map: forecast / explore and its actions. On phones this is all the collapsed
 * sheet shows, so it stays short, and its height never changes: results are read out on the map.
 */
export function PanelSummary({ actions }: { actions?: ReactNode }) {
  const { t } = useTranslation('app');
  const clearAll = useSetAtom(clearAllAtom);
  const canClear = useAtomValue(canClearAtom);

  return (
    <>
      <div className="flex items-center gap-2">
        <AppModeToggle className="flex-1" />
        {actions}
        <Button
          className="w-10 px-0 md:w-auto md:px-3"
          onClick={clearAll}
          disabled={!canClear}
          aria-label={t('clear')}
        >
          <span className="md:hidden">
            <TrashIcon />
          </span>
          <span className="hidden md:inline">{t('clear')}</span>
        </Button>
      </div>
    </>
  );
}

interface SectionProps {
  title: string;
  /** Next to the title, e.g. an InfoTip. */
  info?: ReactNode;
  children: ReactNode;
}

function Section({ title, info, children }: SectionProps) {
  return (
    <section className="pt-5 first:pt-2">
      {/* Fixed height: the tap target of the info button overflows it rather than spacing the title out. */}
      <div className="flex h-5 items-center gap-0.5">
        <h2 className="text-xs font-semibold tracking-wide text-hint uppercase">{title}</h2>
        {info}
      </div>
      <div>{children}</div>
    </section>
  );
}

/** Settings, grouped by what they affect. */
export function PanelSettings() {
  const { t } = useTranslation('app');
  const exploring = useAtomValue(appModeAtom) === 'explore';
  return (
    <div className="pb-4">
      {!exploring && (
        <Section title={t('sections.wind')} info={<WindHelp />}>
          <WindLegend />
        </Section>
      )}
      {exploring && (
        <Section
          title={t('sections.explore')}
          info={
            <InfoTip label={t('exploreHelp.label')}>
              {/* Mode names in italics: they refer to the Fly / Hike setting. */}
              <Trans t={t} i18nKey="exploreHelp.text" components={{ mode: <em /> }} />
            </InfoTip>
          }
        >
          <ModeField />
          <GlideRatioField />
          <ClearanceField />
        </Section>
      )}
      <Section title={t('sections.settings')}>
        <BasemapField />
        <TrailsField />
        <StationsField />
        <UnitsField />
      </Section>
    </div>
  );
}
